import React, { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MapPin, Home, ChevronDown } from "lucide-react";

export function MemberFormFields({ form, setForm }) {
  const [showNameParts, setShowNameParts] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="full_name">Full name (as shown)</Label>
        <Input
          id="full_name"
          data-testid="member-name-input"
          value={form.full_name || ""}
          onChange={set("full_name")}
          placeholder="e.g. Mary Johnson"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="nickname">Nickname / also-known-as</Label>
        <Input
          id="nickname"
          data-testid="member-nickname-input"
          value={form.nickname || ""}
          onChange={set("nickname")}
          placeholder='e.g. "Dell"'
        />
      </div>

      <button
        type="button"
        onClick={() => setShowNameParts((s) => !s)}
        data-testid="toggle-name-parts"
        className="flex items-center gap-1 text-sm font-medium text-[#082D4A] hover:text-[#08223A]"
      >
        <ChevronDown size={15} className={`transition-transform ${showNameParts ? "rotate-180" : ""}`} />
        More name details
      </button>

      {showNameParts && (
        <div className="space-y-3 rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label htmlFor="first_name" className="text-xs">First</Label>
              <Input id="first_name" data-testid="member-first-input" value={form.first_name || ""} onChange={set("first_name")} className="bg-white" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="middle_name" className="text-xs">Middle</Label>
              <Input id="middle_name" data-testid="member-middle-input" value={form.middle_name || ""} onChange={set("middle_name")} className="bg-white" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="last_name" className="text-xs">Last</Label>
              <Input id="last_name" data-testid="member-last-input" value={form.last_name || ""} onChange={set("last_name")} className="bg-white" />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="former_name" className="text-xs">Former / maiden name</Label>
            <Input id="former_name" data-testid="member-former-input" value={form.former_name || ""} onChange={set("former_name")} className="bg-white" placeholder="e.g. surname before marriage" />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Gender</Label>
        <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
          <SelectTrigger data-testid="member-gender-select">
            <SelectValue placeholder="Select gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="birth_date">Born</Label>
          <Input id="birth_date" data-testid="member-birthdate-input" value={form.birth_date || ""} onChange={set("birth_date")} placeholder="YYYY or YYYY-MM-DD" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="death_date">Died (optional)</Label>
          <Input id="death_date" data-testid="member-deathdate-input" value={form.death_date || ""} onChange={set("death_date")} placeholder="YYYY" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="origin_place" className="flex items-center gap-1.5">
          <MapPin size={14} className="text-[#D4A017]" /> From / grew up in
        </Label>
        <Input id="origin_place" data-testid="member-origin-input" value={form.origin_place || ""} onChange={set("origin_place")} placeholder="e.g. Old Harbour, Jamaica" />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="current_place" className="flex items-center gap-1.5">
          <Home size={14} className="text-[#082D4A]" /> Currently lives in
        </Label>
        <Input id="current_place" data-testid="member-current-input" value={form.current_place || ""} onChange={set("current_place")} placeholder="e.g. Winnipeg, Canada" />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="bio">Notes / short bio</Label>
        <Textarea id="bio" data-testid="member-bio-input" value={form.bio || ""} onChange={set("bio")} rows={3} placeholder="A few words about this person…" />
      </div>
    </div>
  );
}
